// SOVRANOVA — Region setting.
// Lets the user tell SOVRANOVA which region/jurisdiction they're in, so the
// backend can apply the right AI-disclosure and affect-sensing rules (see
// backend/services/compliance/jurisdictionPolicy.js). Persisted directly to
// localStorage (not the settings_v1 blob) under `sovranova_region`; the
// shared API client (frontend/src/services/api.js) reads this key and sends
// it as the x-sovranova-region header on every request. Never inferred from
// IP or browser locale — the user always picks it explicitly, or leaves it
// unset for the permissive default. Since the international beta wave (Sep 2026)
// the choice is ALSO persisted to the person's profile (PUT /api/auth/region)
// so it follows them to their other devices; the local write stays so the
// crisis lifeline keeps working offline. An unset choice clears the profile
// value to unknown — unknown never resolves to a country.
import { useState } from 'react'
import { api } from '../../services/api'
import { getCrisisResources, resolveRegion, HELPLINE_DIRECTORY_URL } from '../../services/crisisResources'

const STORAGE_KEY = 'sovranova_region'

const OPTIONS = [
  { value: '',      label: 'Automatic / not set' },
  { value: 'AU',    label: 'Australia' },
  { value: 'US-IL', label: 'United States — Illinois' },
  { value: 'US-NV', label: 'United States — Nevada' },
  { value: 'US-UT', label: 'United States — Utah' },
  { value: 'US-CA', label: 'United States — California' },
  { value: 'GB',    label: 'United Kingdom' },
  { value: 'IE',    label: 'Ireland' },
  { value: 'CA',    label: 'Canada' },
  { value: 'NZ',    label: 'New Zealand' },
  // 112 is the emergency number in every EU/EEA state, so one option serves
  // them all correctly until per-country helplines are verified and added.
  { value: 'EU',    label: 'Europe (other) — 112' },
  { value: 'XX',    label: 'Other / elsewhere' },
]

function readRegion() {
  try { return localStorage.getItem(STORAGE_KEY) || '' } catch { return '' }
}

export function RegionPicker() {
  const [region, setRegion] = useState(readRegion)

  const onChange = (e) => {
    const value = e.target.value
    setRegion(value)
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value)
      else localStorage.removeItem(STORAGE_KEY)
    } catch {}
    // Best-effort server persistence; a failure (offline, signed out) must never
    // block the local choice, which the lifeline reads directly.
    api.put('/auth/region', { region: value || null }).catch(() => {})
  }

  return (
    <div>
      <p style={S.label}>REGION</p>
      <p style={S.hint}>
        <strong>This decides which emergency and crisis services you are shown</strong>,
        as well as the AI-disclosure and privacy rules for your area. Please keep it
        accurate — and update it if you move or travel. Never detected automatically:
        this is your choice alone. It is saved to your account and follows you to your
        other devices.
      </p>
      <select value={region} onChange={onChange} style={S.select} aria-label="Region">
        {OPTIONS.map(o => (
          <option key={o.value || 'auto'} value={o.value}>{o.label}</option>
        ))}
      </select>
      <CrisisPreview />
    </div>
  )
}

// Shows the user exactly which services this setting gives them, so the choice
// is verifiable rather than a guess. Re-reads on every render of the panel.
function CrisisPreview() {
  const res = getCrisisResources(resolveRegion())
  return (
    <p style={S.preview}>
      You'll be shown: <strong>Emergency {res.emergency.number}</strong>
      {res.primary.tel ? <> · {res.primary.label} {res.primary.number}</> : null}
      {' '}· plus{' '}
      <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={S.link}>
        a worldwide helpline directory
      </a>.
    </p>
  )
}

const S = {
  // Organic contrast fix (2026-08-14): cream drawer — dark ink text.
  label:  { fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--og-a700, #8c491a)', margin: '1.25rem 0 0.5rem' },
  hint:   { fontSize: '0.74rem', color: 'var(--og-n700, #645c50)', margin: '0 0 0.6rem', lineHeight: 1.55 },
  select: {
    width: '100%', padding: '0.6rem 0.9rem', borderRadius: '999px',
    background: '#fff', border: '1px solid rgba(32,30,29,0.14)',
    color: 'var(--og-n900, #2e2b25)', fontSize: '0.88rem', fontFamily: 'inherit', cursor: 'pointer',
  },
  preview: { fontSize: '0.74rem', color: 'var(--og-n700, #645c50)', margin: '0.55rem 0 0', lineHeight: 1.55 },
  link:    { color: 'var(--og-a700, #8c491a)', textDecoration: 'underline' },
}
